/**
 * Holder Groups API client. Each write helper takes a `signMessage` from
 * the wallet adapter, builds the canonical message
 * `slurp-group:<action>:<mint>:<ts>`, signs it, and POSTs with the headers
 * the backend's requireSig middleware expects.
 */
import bs58 from "bs58";

const API_BASE =
  process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";

async function getJson(path, opts = {}) {
  // Important: spread `rest` first, then merge headers — otherwise an
  // outer `...opts` would clobber the Content-Type we add here, Express
  // wouldn't parse the JSON body, and every signed write would 400 with
  // "missing_*".
  const { headers: extraHeaders, ...rest } = opts;
  const r = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: { "Content-Type": "application/json", ...(extraHeaders || {}) },
  });
  if (!r.ok) {
    // If the backend supplied a `message` field (rate limits etc.), use it
    // verbatim so the UI can show the friendly text instead of the raw
    // Slurp API blob. Otherwise fall back to a generic message but attach
    // the parsed body to the error so callers can branch on `error` codes
    // (e.g. `insufficient_holding`) and build a nicer message themselves.
    let json = null;
    try { json = await r.json(); } catch { /* ignore */ }
    const err = new Error(json?.message || `Slurp API ${r.status} ${path}: ${JSON.stringify(json ?? "")}`);
    err.body = json;
    err.status = r.status;
    throw err;
  }
  return r.json();
}

// signMessage is the wallet adapter's signMessage (Uint8Array → Uint8Array).
async function signedHeaders({ wallet, signMessage, action, mint }) {
  if (!wallet || !signMessage) throw new Error("wallet not connected");
  const ts = Date.now();
  const msg = new TextEncoder().encode(`slurp-group:${action}:${mint}:${ts}`);
  const sigBytes = await signMessage(msg);
  return {
    "X-Wallet-Address": wallet.toBase58 ? wallet.toBase58() : String(wallet),
    "X-Wallet-Ts": String(ts),
    "X-Wallet-Sig": bs58.encode(sigBytes),
  };
}

export const slurpGroups = {
  // Reads — no auth. `viewer` is optional and only adds the connected
  // wallet's poll vote to each thread for instant "you voted X" UI.
  list: ({ sort = "activity", q = "", limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams({ sort, limit: String(limit), offset: String(offset) });
    if (q) params.set("q", q);
    return getJson(`/api/groups?${params.toString()}`);
  },
  get: (mint) => getJson(`/api/groups/${mint}`),
  threads: (mint, { limit = 20, offset = 0, viewer } = {}) => {
    const v = viewer ? `&viewer=${encodeURIComponent(viewer)}` : "";
    return getJson(`/api/groups/${mint}/threads?limit=${limit}&offset=${offset}${v}`);
  },
  thread: (mint, id, { viewer } = {}) => {
    const v = viewer ? `?viewer=${encodeURIComponent(viewer)}` : "";
    return getJson(`/api/groups/${mint}/threads/${id}${v}`);
  },
  members: (mint, { limit = 50, offset = 0 } = {}) =>
    getJson(`/api/groups/${mint}/members?limit=${limit}&offset=${offset}`),
  membership: (mint, wallet) =>
    getJson(`/api/groups/${mint}/membership/${wallet}`),

  // Writes.
  create: async ({ mint, wallet, signMessage, banner_url, description, min_tokens_to_join }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "create", mint });
    return getJson(`/api/groups/${mint}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ banner_url, description, min_tokens_to_join }),
    });
  },
  update: async ({ mint, wallet, signMessage, banner_url, description, rules, min_tokens_to_join }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "update", mint });
    return getJson(`/api/groups/${mint}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ banner_url, description, rules, min_tokens_to_join }),
    });
  },
  join: async ({ mint, wallet, signMessage }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "join", mint });
    return getJson(`/api/groups/${mint}/join`, { method: "POST", headers });
  },
  leave: async ({ mint, wallet, signMessage }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "leave", mint });
    return getJson(`/api/groups/${mint}/leave`, { method: "POST", headers });
  },
  postThread: async ({ mint, wallet, signMessage, title, body, image_url }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "thread", mint });
    return getJson(`/api/groups/${mint}/threads`, {
      method: "POST",
      headers,
      body: JSON.stringify({ title, body, image_url }),
    });
  },
  postPoll: async ({ mint, wallet, signMessage, title, body, options, closes_at }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "poll", mint });
    return getJson(`/api/groups/${mint}/polls`, {
      method: "POST",
      headers,
      body: JSON.stringify({ title, body, options, closes_at }),
    });
  },
  vote: async ({ mint, wallet, signMessage, threadId, option_index }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "vote", mint });
    return getJson(`/api/groups/${mint}/polls/${threadId}/vote`, {
      method: "POST",
      headers,
      body: JSON.stringify({ option_index }),
    });
  },
  reactThread: async ({ mint, wallet, signMessage, threadId, kind }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "like_thread", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}/like`, {
      method: "POST",
      headers,
      body: JSON.stringify({ kind }),
    });
  },
  flagThread: async ({ mint, wallet, signMessage, threadId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "flag_thread", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}/flag`, { method: "POST", headers });
  },
  flagComment: async ({ mint, wallet, signMessage, commentId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "flag_comment", mint });
    return getJson(`/api/groups/${mint}/comments/${commentId}/flag`, { method: "POST", headers });
  },
  holderRanks: (mint, { top = 200 } = {}) =>
    getJson(`/api/groups/${mint}/holder-ranks?top=${top}`),
  reactComment: async ({ mint, wallet, signMessage, commentId, kind }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "like_comment", mint });
    return getJson(`/api/groups/${mint}/comments/${commentId}/like`, {
      method: "POST",
      headers,
      body: JSON.stringify({ kind }),
    });
  },
  editThread: async ({ mint, wallet, signMessage, threadId, title, body, image_url }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "edit_thread", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ title, body, image_url }),
    });
  },
  deleteThread: async ({ mint, wallet, signMessage, threadId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "delete_thread", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}`, { method: "DELETE", headers });
  },
  editComment: async ({ mint, wallet, signMessage, commentId, body, image_url }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "edit_comment", mint });
    return getJson(`/api/groups/${mint}/comments/${commentId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ body, image_url }),
    });
  },
  deleteComment: async ({ mint, wallet, signMessage, commentId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "delete_comment", mint });
    return getJson(`/api/groups/${mint}/comments/${commentId}`, { method: "DELETE", headers });
  },
  cleanupNonHolders: async ({ mint, wallet, signMessage }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "cleanup", mint });
    return getJson(`/api/groups/${mint}/cleanup-non-holders`, { method: "POST", headers });
  },
  promote: async ({ mint, wallet, signMessage, target }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "promote", mint });
    return getJson(`/api/groups/${mint}/promote`, {
      method: "POST",
      headers,
      body: JSON.stringify({ wallet: target }),
    });
  },
  demote: async ({ mint, wallet, signMessage, target }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "demote", mint });
    return getJson(`/api/groups/${mint}/demote`, {
      method: "POST",
      headers,
      body: JSON.stringify({ wallet: target }),
    });
  },
  postComment: async ({ mint, wallet, signMessage, threadId, body, image_url, parent_id }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "comment", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}/comments`, {
      method: "POST",
      headers,
      body: JSON.stringify({ body, image_url, parent_id }),
    });
  },
  pinThread: async ({ mint, wallet, signMessage, threadId, pinned }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "pin", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}/pin`, {
      method: "POST",
      headers,
      body: JSON.stringify({ pinned }),
    });
  },
  hideThread: async ({ mint, wallet, signMessage, threadId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "hide_thread", mint });
    return getJson(`/api/groups/${mint}/threads/${threadId}/hide`, { method: "POST", headers });
  },
  hideComment: async ({ mint, wallet, signMessage, commentId }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "hide_comment", mint });
    return getJson(`/api/groups/${mint}/comments/${commentId}/hide`, { method: "POST", headers });
  },
  kick: async ({ mint, wallet, signMessage, target }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "kick", mint });
    return getJson(`/api/groups/${mint}/kick`, {
      method: "POST",
      headers,
      body: JSON.stringify({ wallet: target }),
    });
  },
  ban: async ({ mint, wallet, signMessage, target, reason }) => {
    const headers = await signedHeaders({ wallet, signMessage, action: "ban", mint });
    return getJson(`/api/groups/${mint}/ban`, {
      method: "POST",
      headers,
      body: JSON.stringify({ wallet: target, reason }),
    });
  },
};
